import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, TrendUp, TrendDown, ChartLine, Info, Newspaper, Lightning, Globe, Bank, Flame, ArrowSquareOut, Clock, Spinner, Star } from '@phosphor-icons/react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Area, AreaChart } from 'recharts';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { formatCurrency, formatPercent, formatNumber } from '../lib/utils';
import { useAuth } from '../context/AuthContext';
import { useCurrency, CURRENCY_META } from '../context/CurrencyContext';
import api from '../lib/api';
import { toast } from 'sonner';

// Helper to format time ago
const timeAgo = (dateStr) => {
  if (!dateStr) return '';
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  return `${days}d ago`;
};

// Generate AI summary based on stock data
const generateSummary = (stock) => {
  const isPositive = parseFloat(stock.change_percent) >= 0;
  const changePercent = Math.abs(parseFloat(stock.change_percent)).toFixed(2);
  const companyName = stock.company_name || stock.name || stock.symbol;
  
  const sentiment = isPositive ? 'bullish' : 'bearish';
  const movement = isPositive ? 'gained' : 'lost';
  const outlook = isPositive ? 'positive momentum' : 'selling pressure';
  
  return {
    quickTake: `${companyName} (${stock.symbol}) ${movement} ${changePercent}% today, showing ${sentiment} sentiment in the market.`,
    keyPoints: [
      `Stock is currently trading at ${formatCurrency(stock.price)}`,
      `Daily trading volume: ${formatNumber(stock.volume)} shares`,
      `Market shows ${outlook} for ${stock.symbol}`,
      isPositive ? 'Buyers are in control of the price action' : 'Sellers are dominating current trading',
    ],
    recommendation: changePercent > 5 
      ? (isPositive ? '🚀 Strong upward momentum - consider taking profits' : '⚠️ Significant drop - wait for stabilization')
      : (isPositive ? '📈 Steady gains - good for long-term holders' : '📉 Minor pullback - potential buying opportunity'),
    riskLevel: changePercent > 5 ? 'High' : changePercent > 2 ? 'Medium' : 'Low'
  };
};

const PEInfoPopup = ({ onClose }) => (
  <div
    className="fixed inset-0 z-[60] flex items-center justify-center p-4"
    data-testid="pe-info-popup"
  >
    <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={onClose} />
    <motion.div
      initial={{ opacity: 0, scale: 0.92, y: 12 }}
      animate={{ opacity: 1, scale: 1, y: 0 }}
      className="relative w-full max-w-md glass-card p-6 border border-gold/30"
    >
      <button
        onClick={onClose}
        className="absolute top-3 right-3 text-zinc-400 hover:text-white transition-colors"
        data-testid="pe-info-close"
      >
        <X size={20} />
      </button>
      <div className="flex items-center gap-2 mb-4">
        <div className="w-9 h-9 rounded-full bg-gold/20 flex items-center justify-center">
          <Info size={20} weight="bold" className="text-gold" />
        </div>
        <h3 className="text-lg font-unbounded font-bold text-white">What's a P/E Ratio?</h3>
      </div>
      <div className="space-y-4 text-sm text-zinc-300 leading-relaxed">
        <p>
          <span className="text-gold font-bold">P/E = Price ÷ Earnings.</span> It tells you how many
          dollars you're paying for every <span className="text-white font-bold">$1 of profit</span> the
          company makes in a year.
        </p>
        <div className="p-3 rounded-xl bg-gold/10 border border-gold/20">
          <p className="text-xs text-gold font-bold uppercase tracking-wide mb-1">Example</p>
          <p>
            If a stock costs <span className="text-white font-bold">$100</span> and the company earns{' '}
            <span className="text-white font-bold">$5 per share</span> a year, the P/E is{' '}
            <span className="text-gold font-bold">20</span> — you're paying $20 for each $1 of yearly profit.
          </p>
        </div>
        <div className="space-y-2">
          <p className="flex items-start gap-2">
            <span className="text-emerald-400 font-bold shrink-0">High P/E (30+):</span>
            <span>Investors expect big future growth — but the stock might be pricey.</span>
          </p>
          <p className="flex items-start gap-2">
            <span className="text-red-400 font-bold shrink-0">Low P/E (&lt;15):</span>
            <span>Could be a bargain… or a sign investors expect trouble ahead.</span>
          </p>
        </div>
        <p className="text-xs text-zinc-500 border-t border-white/10 pt-3">
          💡 Pro tip: compare a stock's P/E to <span className="text-zinc-300 font-medium">similar companies in the same industry</span> — not random ones!
        </p>
      </div>
    </motion.div>
  </div>
);

const StockDetailModal = ({ stock, onClose }) => {
  const { user, updateBalance, fetchUser } = useAuth();
  const { format: fmtCcy, formatNative, displayCcy } = useCurrency();
  const usdPrice = stock.price_usd ?? stock.price ?? 0;
  const nativeCcy = stock.native_currency || 'USD';
  const showNativePrice = nativeCcy !== 'USD' && nativeCcy !== displayCcy;
  const [activeTab, setActiveTab] = useState('summary');
  const [tradeTab, setTradeTab] = useState('buy');
  const [shares, setShares] = useState('');
  const [loading, setLoading] = useState(false);
  const [history, setHistory] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(true);
  const [timeRange, setTimeRange] = useState('1M');
  const [stockNews, setStockNews] = useState([]);
  const [aiSummary, setAiSummary] = useState('');
  const [newsLoading, setNewsLoading] = useState(false);
  const [summary, setSummary] = useState(null);
  const [inWatchlist, setInWatchlist] = useState(false);
  const [watchlistBusy, setWatchlistBusy] = useState(false);
  const [fundamentals, setFundamentals] = useState(null);
  const [showPEInfo, setShowPEInfo] = useState(false);

  useEffect(() => {
    fetchHistory(timeRange);
    setSummary(generateSummary(stock));
  }, [stock.symbol, timeRange]);

  // Fetch fundamentals (P/E ratio) — cached 6h server-side
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await api.get(`/stocks/${stock.symbol}/fundamentals`);
        if (!cancelled) setFundamentals(res.data);
      } catch {
        /* ignore — show N/A */
      }
    })();
    return () => { cancelled = true; };
  }, [stock.symbol]);

  const peRaw = fundamentals?.pe_ratio ?? (Number(stock.pe_ratio) > 0 ? Number(stock.pe_ratio) : null);
  const peDisplay = peRaw && peRaw > 0 ? Number(peRaw).toFixed(1) : 'N/A';

  // Fetch watchlist status when modal opens or symbol changes
  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (user?.is_guest) return;
      try {
        const res = await api.get('/watchlist');
        if (cancelled) return;
        const list = res.data?.symbols || [];
        setInWatchlist(list.includes(stock.symbol));
      } catch {
        /* ignore */
      }
    })();
    return () => { cancelled = true; };
  }, [stock.symbol, user?.is_guest]);

  const toggleWatchlist = async () => {
    if (user?.is_guest) {
      toast.error('Sign up free to save a watchlist');
      return;
    }
    if (watchlistBusy) return;
    setWatchlistBusy(true);
    const wasIn = inWatchlist;
    setInWatchlist(!wasIn); // optimistic
    try {
      if (wasIn) {
        await api.delete(`/watchlist/${stock.symbol}`);
        toast.success(`${stock.symbol} removed from watchlist`);
      } else {
        await api.post(`/watchlist/${stock.symbol}`);
        toast.success(`${stock.symbol} added to watchlist ⭐`);
      }
    } catch (err) {
      setInWatchlist(wasIn); // revert
      const msg = err?.response?.data?.detail || 'Could not update watchlist';
      toast.error(typeof msg === 'string' ? msg : 'Could not update watchlist');
    } finally {
      setWatchlistBusy(false);
    }
  };

  // Fetch real news when News tab is opened
  useEffect(() => {
    if (activeTab === 'news' && stockNews.length === 0 && !newsLoading) {
      fetchStockNews();
    }
  }, [activeTab]);

  const fetchStockNews = async () => {
    setNewsLoading(true);
    try {
      const res = await api.get(`/news/stock/${stock.symbol}?limit=8`);
      setStockNews(res.data.articles || []);
      setAiSummary(res.data.ai_summary || '');
    } catch {
      setStockNews([]);
    } finally {
      setNewsLoading(false);
    }
  };

  const fetchHistory = async (range) => {
    setLoadingHistory(true);
    try {
      const response = await api.get(`/stocks/${stock.symbol}/history?range=${range}`);
      setHistory(response.data.history);
    } catch (error) {
      console.error('Failed to load history');
    } finally {
      setLoadingHistory(false);
    }
  };

  const timeRanges = [
    { label: '1D', value: '1D' },
    { label: '1W', value: '1W' },
    { label: '1M', value: '1M' },
    { label: '3M', value: '3M' },
    { label: '6M', value: '6M' },
    { label: '1Y', value: '1Y' },
  ];

  const handleTrade = async () => {
    if (!shares || parseFloat(shares) <= 0) {
      toast.error('Please enter a valid number of shares');
      return;
    }

    setLoading(true);
    try {
      const endpoint = tradeTab === 'buy' ? '/trades/buy' : '/trades/sell';
      const response = await api.post(endpoint, {
        symbol: stock.symbol,
        shares: parseFloat(shares)
      });

      updateBalance(response.data.balance);
      toast.success(
        tradeTab === 'buy'
          ? `Successfully bought ${shares} shares of ${stock.symbol}`
          : `Successfully sold ${shares} shares of ${stock.symbol}`
      );
      await fetchUser();
      onClose();
    } catch (error) {
      toast.error(error.response?.data?.detail || `Failed to ${tradeTab} stock`);
    } finally {
      setLoading(false);
    }
  };

  const totalCost = shares ? parseFloat(shares) * usdPrice : 0;
  const canAfford = totalCost <= (user?.balance || 0);
  const isPositive = parseFloat(stock.change_percent) >= 0;

  const CustomTooltip = ({ active, payload }) => {
    if (active && payload && payload.length) {
      return (
        <div className="glass-card p-3 border border-white/20">
          <p className="text-xs text-zinc-400 mb-1">{payload[0].payload.date}</p>
          <p className="text-sm font-mono font-bold text-white">
            {formatCurrency(payload[0].value)}
          </p>
        </div>
      );
    }
    return null;
  };

  return (
    <AnimatePresence>
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-testid="stock-detail-modal">
        {/* Backdrop */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={onClose}
          className="absolute inset-0 bg-black/80 backdrop-blur-sm"
        />

        {/* Modal */}
        <motion.div
          initial={{ opacity: 0, scale: 0.9, y: 20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.9, y: 20 }}
          className="relative w-full max-w-6xl glass-card p-8 max-h-[90vh] overflow-y-auto"
        >
          <button
            onClick={onClose}
            className="absolute top-4 right-4 text-zinc-400 hover:text-white transition-colors z-10"
            data-testid="close-detail-modal"
          >
            <X size={24} />
          </button>

          {/* Header */}
          <div className="mb-6">
            <div className="flex items-start justify-between mb-4">
              <div>
                <div className="flex items-center gap-3 mb-2 flex-wrap">
                  <h2 className="text-4xl font-unbounded font-bold text-white">
                    {stock.symbol}
                  </h2>
                  <button
                    onClick={toggleWatchlist}
                    disabled={watchlistBusy}
                    title={inWatchlist ? 'Remove from watchlist' : 'Add to watchlist'}
                    aria-label={inWatchlist ? 'Remove from watchlist' : 'Add to watchlist'}
                    className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full border text-sm font-semibold transition-all ${
                      inWatchlist
                        ? 'bg-gold/20 border-gold/40 text-gold hover:bg-gold/30'
                        : 'bg-white/5 border-white/10 text-zinc-300 hover:bg-white/10 hover:text-white'
                    } disabled:opacity-50`}
                    data-testid="watchlist-toggle-button"
                  >
                    <Star size={16} weight={inWatchlist ? 'fill' : 'regular'} />
                    {inWatchlist ? 'Watching' : 'Watchlist'}
                  </button>
                </div>
                <p className="text-xl text-zinc-400 mb-1">{stock.company_name || stock.name}</p>
                <div className="flex items-center gap-3 flex-wrap">
                  <span className="px-3 py-1 rounded-full bg-gold/20 text-gold text-xs font-bold">
                    {stock.sector || 'Stocks'}
                  </span>
                </div>
              </div>
              <div className="text-right">
                <p className="font-mono text-3xl font-bold text-white mb-1" data-testid="modal-price">
                  {fmtCcy(usdPrice)}
                </p>
                {showNativePrice && (
                  <p className="font-mono text-xs text-zinc-500 mb-1" data-testid="modal-native-price">
                    {CURRENCY_META[nativeCcy]?.flag} {formatNative(stock.price, nativeCcy)} native
                  </p>
                )}
                <p className={`font-mono text-lg font-bold ${isPositive ? 'text-success' : 'text-destructive'}`}>
                  {isPositive ? '+' : ''}{parseFloat(stock.change_percent).toFixed(2)}%
                </p>
              </div>
            </div>
          </div>

          {/* Tabs - Summary, Overview, Trade, News */}
          <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
            <TabsList className="grid w-full grid-cols-4 mb-6 bg-white/5 p-1 rounded-xl">
              <TabsTrigger value="summary" className="data-[state=active]:bg-gradient-to-r data-[state=active]:from-yellow-400 data-[state=active]:to-yellow-600 data-[state=active]:text-black rounded-lg">
                Summary
              </TabsTrigger>
              <TabsTrigger value="overview" className="data-[state=active]:bg-gradient-to-r data-[state=active]:from-yellow-400 data-[state=active]:to-yellow-600 data-[state=active]:text-black rounded-lg">
                Overview
              </TabsTrigger>
              <TabsTrigger value="trade" className="data-[state=active]:bg-gradient-to-r data-[state=active]:from-yellow-400 data-[state=active]:to-yellow-600 data-[state=active]:text-black rounded-lg">
                Trade
              </TabsTrigger>
              <TabsTrigger value="news" className="data-[state=active]:bg-gradient-to-r data-[state=active]:from-yellow-400 data-[state=active]:to-yellow-600 data-[state=active]:text-black rounded-lg">
                News
              </TabsTrigger>
            </TabsList>

            {/* Summary Tab */}
            <TabsContent value="summary" className="space-y-6">
              {summary && (
                <>
                  {/* Quick Take */}
                  <div className="glass-card p-6">
                    <div className="flex items-center gap-2 mb-4">
                      <Lightning size={24} className="text-gold" weight="fill" />
                      <h3 className="text-xl font-unbounded font-bold text-white">Quick Take</h3>
                    </div>
                    <p className="text-lg text-zinc-300 leading-relaxed">{summary.quickTake}</p>
                    
                    <div className="mt-4 p-4 rounded-xl bg-gradient-to-r from-gold/10 to-yellow-500/10 border border-gold/30">
                      <p className="text-gold font-bold">{summary.recommendation}</p>
                    </div>
                  </div>

                  {/* Key Points */}
                  <div className="glass-card p-6">
                    <h3 className="text-lg font-bold text-white mb-4">Key Points</h3>
                    <ul className="space-y-3">
                      {summary.keyPoints.map((point, i) => (
                        <li key={i} className="flex items-start gap-3">
                          <span className="text-gold">•</span>
                          <span className="text-zinc-300">{point}</span>
                        </li>
                      ))}
                    </ul>
                  </div>

                  {/* Risk & Stats */}
                  <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                    <div className="glass p-4 rounded-xl text-center">
                      <p className="text-xs text-zinc-500 uppercase mb-1 flex items-center justify-center gap-1">
                        P/E Ratio
                        <button
                          onClick={() => setShowPEInfo(true)}
                          className="text-zinc-500 hover:text-gold transition-colors"
                          title="What is a P/E ratio?"
                          aria-label="What is a P/E ratio?"
                          data-testid="pe-info-button-summary"
                        >
                          <Info size={14} weight="bold" />
                        </button>
                      </p>
                      <p className="font-mono font-bold text-gold text-lg" data-testid="pe-ratio-summary">{peDisplay}</p>
                    </div>
                    <div className="glass p-4 rounded-xl text-center">
                      <p className="text-xs text-zinc-500 uppercase mb-1">Risk Level</p>
                      <p className={`font-bold text-lg ${
                        summary.riskLevel === 'High' ? 'text-red-400' : 
                        summary.riskLevel === 'Medium' ? 'text-yellow-400' : 'text-emerald-400'
                      }`}>{summary.riskLevel}</p>
                    </div>
                    <div className="glass p-4 rounded-xl text-center">
                      <p className="text-xs text-zinc-500 uppercase mb-1">Sentiment</p>
                      <p className={`font-bold text-lg ${isPositive ? 'text-emerald-400' : 'text-red-400'}`}>
                        {isPositive ? 'Bullish' : 'Bearish'}
                      </p>
                    </div>
                    <div className="glass p-4 rounded-xl text-center">
                      <p className="text-xs text-zinc-500 uppercase mb-1">Volume</p>
                      <p className="font-mono font-bold text-white">{formatNumber(stock.volume)}</p>
                    </div>
                    <div className="glass p-4 rounded-xl text-center">
                      <p className="text-xs text-zinc-500 uppercase mb-1">Day Range</p>
                      <p className="font-mono font-bold text-white text-sm">
                        {formatCurrency(stock.low || stock.price * 0.98)} - {formatCurrency(stock.high || stock.price * 1.02)}
                      </p>
                    </div>
                  </div>
                </>
              )}
            </TabsContent>

            {/* Overview Tab */}
            <TabsContent value="overview" className="space-y-6">
              {/* Chart */}
              <div className="glass-card p-6">
                <div className="flex items-center justify-between mb-6">
                  <div className="flex items-center gap-2">
                    <ChartLine size={24} className="text-gold" weight="duotone" />
                    <h3 className="text-xl font-unbounded font-bold text-white">Price History</h3>
                  </div>
                  
                  <div className="flex gap-2">
                    {timeRanges.map((range) => (
                      <button
                        key={range.value}
                        onClick={() => setTimeRange(range.value)}
                        className={`px-3 py-1 rounded-lg text-sm font-bold transition-colors ${
                          timeRange === range.value
                            ? 'bg-gradient-to-r from-yellow-400 to-yellow-600 text-black'
                            : 'bg-white/10 text-zinc-400 hover:bg-white/20 hover:text-white'
                        }`}
                      >
                        {range.label}
                      </button>
                    ))}
                  </div>
                </div>
                
                {loadingHistory ? (
                  <div className="h-64 flex items-center justify-center">
                    <div className="text-center">
                      <div className="inline-block w-12 h-12 border-4 border-gold border-t-transparent rounded-full animate-spin mb-4" />
                      <p className="text-zinc-400">Loading chart...</p>
                    </div>
                  </div>
                ) : (
                  <ResponsiveContainer width="100%" height={300}>
                    <AreaChart data={history}>
                      <defs>
                        <linearGradient id="colorPrice" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%" stopColor={isPositive ? "#00FF94" : "#FF0055"} stopOpacity={0.3}/>
                          <stop offset="95%" stopColor={isPositive ? "#00FF94" : "#FF0055"} stopOpacity={0}/>
                        </linearGradient>
                      </defs>
                      <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.1)" />
                      <XAxis dataKey="date" stroke="#71717a" tick={{ fill: '#71717a', fontSize: 10 }} />
                      <YAxis stroke="#71717a" tick={{ fill: '#71717a', fontSize: 10 }} tickFormatter={(v) => `$${v}`} />
                      <Tooltip content={<CustomTooltip />} />
                      <Area type="monotone" dataKey="close" stroke={isPositive ? "#00FF94" : "#FF0055"} strokeWidth={2} fill="url(#colorPrice)" />
                    </AreaChart>
                  </ResponsiveContainer>
                )}
              </div>

              {/* Key Statistics */}
              <div className="glass-card p-6">
                <h3 className="text-lg font-bold text-white mb-4">Key Statistics</h3>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div>
                    <p className="text-sm text-zinc-400 mb-1">Open</p>
                    <p className="font-mono font-bold text-white">{formatCurrency(stock.open || stock.price)}</p>
                  </div>
                  <div>
                    <p className="text-sm text-zinc-400 mb-1">High</p>
                    <p className="font-mono font-bold text-emerald-400">{formatCurrency(stock.high || stock.price * 1.02)}</p>
                  </div>
                  <div>
                    <p className="text-sm text-zinc-400 mb-1">Low</p>
                    <p className="font-mono font-bold text-red-400">{formatCurrency(stock.low || stock.price * 0.98)}</p>
                  </div>
                  <div>
                    <p className="text-sm text-zinc-400 mb-1">Volume</p>
                    <p className="font-mono font-bold text-white">{formatNumber(stock.volume)}</p>
                  </div>
                  <div>
                    <p className="text-sm text-zinc-400 mb-1 flex items-center gap-1.5">
                      P/E Ratio
                      <button
                        onClick={() => setShowPEInfo(true)}
                        className="text-zinc-500 hover:text-gold transition-colors"
                        title="What is a P/E ratio?"
                        aria-label="What is a P/E ratio?"
                        data-testid="pe-info-button-overview"
                      >
                        <Info size={14} weight="bold" />
                      </button>
                    </p>
                    <p className="font-mono font-bold text-gold" data-testid="pe-ratio-overview">{peDisplay}</p>
                  </div>
                </div>
              </div>
            </TabsContent>

            {/* Trade Tab */}
            <TabsContent value="trade">
              <div className="glass-card p-6">
                <h3 className="text-xl font-unbounded font-bold text-white mb-6">Trade {stock.symbol}</h3>
                
                <div className="flex gap-2 mb-6">
                  <Button
                    onClick={() => setTradeTab('buy')}
                    className={`flex-1 py-4 rounded-xl font-bold ${
                      tradeTab === 'buy'
                        ? 'bg-gradient-to-r from-emerald-500 to-emerald-600 text-white'
                        : 'bg-white/5 text-zinc-400 hover:bg-white/10'
                    }`}
                  >
                    Buy
                  </Button>
                  <Button
                    onClick={() => setTradeTab('sell')}
                    className={`flex-1 py-4 rounded-xl font-bold ${
                      tradeTab === 'sell'
                        ? 'bg-gradient-to-r from-red-500 to-red-600 text-white'
                        : 'bg-white/5 text-zinc-400 hover:bg-white/10'
                    }`}
                  >
                    Sell
                  </Button>
                </div>

                <div className="space-y-6">
                  <div>
                    <label className="block text-sm font-medium text-zinc-300 mb-2">
                      Number of Shares
                    </label>
                    <Input
                      type="number"
                      placeholder="0"
                      value={shares}
                      onChange={(e) => setShares(e.target.value)}
                      className="bg-black/50 border-white/10 focus:border-gold/50 text-white text-lg py-6"
                      min="0"
                      step="0.01"
                    />
                  </div>

                  <div className="glass p-4 rounded-xl space-y-2">
                    <div className="flex justify-between">
                      <span className="text-zinc-400">Price per share</span>
                      <span className="font-mono font-bold text-white">{fmtCcy(usdPrice)}</span>
                    </div>
                    {showNativePrice && (
                      <div className="flex justify-between text-xs">
                        <span className="text-zinc-500">Native price</span>
                        <span className="font-mono text-zinc-400">{formatNative(stock.price, nativeCcy)}</span>
                      </div>
                    )}
                    <div className="flex justify-between">
                      <span className="text-zinc-400">Total Cost</span>
                      <span className="font-mono font-bold text-white">{fmtCcy(totalCost)}</span>
                    </div>
                    <div className="flex justify-between pt-2 border-t border-white/10">
                      <span className="text-zinc-400">Your Balance</span>
                      <span className="font-mono font-bold text-gold">{fmtCcy(user?.balance || 0)}</span>
                    </div>
                  </div>

                  {tradeTab === 'buy' && !canAfford && totalCost > 0 && (
                    <p className="text-red-400 text-sm">Insufficient balance for this trade</p>
                  )}

                  <Button
                    onClick={handleTrade}
                    disabled={loading || !shares || parseFloat(shares) <= 0 || (tradeTab === 'buy' && !canAfford)}
                    className={`w-full py-6 font-bold rounded-xl ${
                      tradeTab === 'buy' 
                        ? 'bg-gradient-to-r from-emerald-500 to-emerald-600' 
                        : 'bg-gradient-to-r from-red-500 to-red-600'
                    } disabled:opacity-50`}
                  >
                    {loading ? 'Processing...' : `${tradeTab === 'buy' ? 'Buy' : 'Sell'} ${shares || '0'} shares`}
                  </Button>
                </div>
              </div>
            </TabsContent>

            {/* News Tab */}
            <TabsContent value="news" className="space-y-4">
              <div className="flex items-center gap-2 mb-4">
                <Newspaper size={24} className="text-gold" />
                <h3 className="text-xl font-unbounded font-bold text-white">Latest News for {stock.symbol}</h3>
              </div>

              {/* AI Summary */}
              {aiSummary && (
                <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="glass-card p-5 border-l-4 border-l-gold mb-4">
                  <div className="flex items-center gap-2 mb-2">
                    <Lightning size={18} weight="fill" className="text-gold" />
                    <span className="text-sm font-bold text-gold">AI Summary</span>
                  </div>
                  <p className="text-zinc-300 text-sm leading-relaxed" data-testid="stock-news-ai-summary">{aiSummary}</p>
                </motion.div>
              )}

              {newsLoading ? (
                <div className="flex items-center justify-center py-12">
                  <div className="inline-block w-10 h-10 border-4 border-gold border-t-transparent rounded-full animate-spin" />
                </div>
              ) : stockNews.length === 0 ? (
                <div className="glass-card p-8 text-center">
                  <p className="text-zinc-400">No recent news for this stock</p>
                </div>
              ) : (
                stockNews.map((article, index) => (
                  <motion.a
                    key={index}
                    href={article.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.08 }}
                    className="block glass-card p-5 hover:border-gold/30 transition-all group"
                    data-testid={`stock-news-item-${index}`}
                  >
                    <div className="flex gap-4">
                      {article.image && (
                        <img
                          src={article.image}
                          alt=""
                          className="w-20 h-20 rounded-lg object-cover flex-shrink-0 hidden sm:block"
                          onError={(e) => { e.target.style.display = 'none'; }}
                        />
                      )}
                      <div className="flex-1 min-w-0">
                        <p className="text-white font-medium mb-2 group-hover:text-gold transition-colors line-clamp-2">{article.title}</p>
                        {article.summary && (
                          <p className="text-zinc-500 text-xs mb-2 line-clamp-2">{article.summary}</p>
                        )}
                        <div className="flex items-center gap-3 text-xs text-zinc-500">
                          <span className="flex items-center gap-1">
                            <Newspaper size={12} />
                            {article.source}
                          </span>
                          <span className="flex items-center gap-1">
                            <Clock size={12} />
                            {timeAgo(article.published)}
                          </span>
                          <ArrowSquareOut size={12} className="ml-auto text-zinc-600 group-hover:text-gold transition-colors" />
                        </div>
                      </div>
                    </div>
                  </motion.a>
                ))
              )}
            </TabsContent>
          </Tabs>

          {showPEInfo && <PEInfoPopup onClose={() => setShowPEInfo(false)} />}
        </motion.div>
      </div>
    </AnimatePresence>
  );
};

export default StockDetailModal;
